/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { bindActionCreators, compose, Dispatch } from "redux";

import { FastField, Field, Form, Formik } from "formik";
import * as Yup from "yup";

import {
  IPersonaProps,
  IPickerItemProps,
  Label,
  NormalPeoplePicker,
  PeoplePickerItem,
  PersonaPresence,
  PrimaryButton,
  Text,
  ValidationState,
} from "@fluentui/react";
import { Stack } from "@fluentui/react/lib/Stack";

import * as AccessControlActions from "Dataflows/AccessControl/AccessControlAction";

import FormikDropdown from "Components/Common/Dropdown/FormikDropdown";
import FormikErrorMessage from "Components/Common/FormikErrorMessage/FormikErrorMessage";
import { accessControlUserStatus } from "Constants/AccessControlConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { peoplePickerSuggestionProps } from "Constants/PeoplePickerConstants";
import { INPUT_REQUIRED } from "Constants/ValidationMessages";
import { IAccessControlCreateEditContainerProps } from "Containers/AccessControl/AccessControlCreateEditContainer/IAccessControlCreateEditContainerProps";
import { getNetworkOperationError } from "Dataflows/AccessControl/AccessControlSelector";
import { AppState } from "Dataflows/Reducers";
import { IWriteAccessControlUser } from "Interfaces/AccessControl/IWriteAccessControlUser";
import { callMsGraphGetUserByEmail } from "Utility/MsGraphApiCall";
import { onInputChange } from "Utility/Utils";

import {
  buttonStackTokens,
  buttonStyles,
  dropdownStyles,
  fieldItemStyles,
  flexStackStyles,
  formStackStyles,
  highlightTextStyles,
  horizontalStackTokens,
  verticalStackTokens,
  SolidDivider,
} from "./AccessControlUserForm.styles";
import { IAccessControlUserFormProps } from "./IAccessControlUserFormProps";
import { IPersonaPropsExtend } from "./IPersonaPropsExtend";

/**
 * Functional component to render the edit access user form.
 * @function
 * @param  {IAccessControlUserFormProps} props The props for the access user form component.
 * @returns {React.ReactElement} The react element.
 */
const AccessControlUserForm = (
  props: IAccessControlUserFormProps & Partial<IAccessControlCreateEditContainerProps>
): React.ReactElement => {
  const {
    actions,
    selectedUser,
    selectedUserId,
    tenant,
    isEditing,
    newUserId,
    networkOperationStatus,
    networkOperationError,
  } = props;

  /**
   * The Validation for the access user form.
   */
  const accessUserSchema = Yup.object().shape({
    pickerUser: Yup.array()
      .required(INPUT_REQUIRED)
      .test("pickerUser", INPUT_REQUIRED, function (value) {
        if (!value || value.length <= 0) {
          return false;
        }
        return true;
      }),
  });

  const picker = React.useRef(null);
  const isFormSubmitting = networkOperationStatus === OperationStatus.Pending;

  /**
   * Search AAD User Info by the input user email.
   * @param {string}  filterText  the user email.
   * @param {IPersonaProps[] | undefined}  currentPersonas  Already selected persons.
   * @returns {IPersonaPropsExtend[] | Promise<IPersonaPropsExtend[]>} Selectable persons.
   */
  const onFilterChanged = (
    filterText: string,
    currentPersonas: IPersonaProps[] | undefined
  ): IPersonaPropsExtend[] | Promise<IPersonaPropsExtend[]> => {
    if (filterText && filterText.endsWith("@microsoft.com")) {
      return callMsGraphGetUserByEmail(filterText).then((value) => {
        if (!value.error && !currentPersonas?.find((p) => p.id === value.id)) {
          return [
            {
              id: value.id,
              imageInitials: `${value.givenName[0]}${value.surname[0]}`,
              text: value.displayName,
              secondaryText: value.userPrincipalName,
              presence: PersonaPresence.none,
              firstName: value.givenName,
              lastName: value.surname,
              emailAddress: value.userPrincipalName,
            },
          ];
        }
      }) as Promise<IPersonaPropsExtend[]>;
    } else {
      return [];
    }
  };

  /**
   * Render item with secondary text for the NormalPeoplePicker.
   */
  const renderItemWithSecondaryText = (props: IPickerItemProps<IPersonaProps>) => {
    const newProps = {
      ...props,
      item: {
        ...props.item,
        ValidationState: ValidationState.valid,
        showSecondaryText: true,
      },
    };

    return <PeoplePickerItem {...newProps} />;
  };

  const defaultSelect = [] as Array<IPersonaPropsExtend>;

  if (isEditing) {
    defaultSelect.push({
      id: selectedUser?.id.toString(),
      imageInitials: `${selectedUser?.firstName[0]}${selectedUser?.lastName[0]}`,
      text: selectedUser?.emailAddress,
      secondaryText: `${selectedUser?.firstName} ${selectedUser?.lastName}`,
      presence: PersonaPresence.none,
      firstName: selectedUser?.firstName as string,
      lastName: selectedUser?.lastName as string,
      emailAddress: selectedUser?.emailAddress as string,
    });
  }

  const canOnlyUpdateStatus = isEditing || (newUserId !== 0 && newUserId !== undefined);

  const showError = (error: string): React.ReactElement => {
    return (
      <>
        <Stack.Item>
          <Text styles={highlightTextStyles}>{error}</Text>
        </Stack.Item>
      </>
    );
  };

  return (
    <Formik
      initialValues={{
        email: selectedUser?.emailAddress,
        pickerUser: defaultSelect,
        status: !isEditing || selectedUser?.isActive ? "true" : "false",
      }}
      validationSchema={accessUserSchema}
      onSubmit={(values, { setSubmitting }): void => {
        setSubmitting(false);
        const isActive = values.status === "true" ? true : false;

        if (!canOnlyUpdateStatus) {
          const user: IWriteAccessControlUser = {
            id: 0,
            firstName: values.pickerUser[0].firstName,
            lastName: values.pickerUser[0].lastName,
            emailAddress: values.pickerUser[0].emailAddress,
            isActive: isActive,
          };
          actions.createUser(tenant, user);
        } else {
          const userId = isEditing ? selectedUserId : newUserId;
          actions.updateUserStatus(tenant, userId as number, isActive);
        }
      }}
    >
      {({ isSubmitting, setFieldValue, isValid }): React.ReactNode => (
        <Form>
          <Stack tokens={verticalStackTokens} styles={formStackStyles}>
            <Stack horizontal wrap={true} tokens={horizontalStackTokens} styles={flexStackStyles}>
              <Stack.Item styles={fieldItemStyles}>
                <Label>Email Address</Label>
                <Field
                  disabled={canOnlyUpdateStatus}
                  component={NormalPeoplePicker}
                  id="email"
                  label="Email Address"
                  name="email"
                  placeholder="Enter Email Address"
                  defaultSelectedItems={defaultSelect}
                  onChange={(emailUser: IPersonaPropsExtend[] | undefined): void => {
                    setFieldValue("pickerUser", emailUser);
                  }}
                  onResolveSuggestions={onFilterChanged}
                  pickerSuggestionsProps={peoplePickerSuggestionProps}
                  className={"ms-PeoplePicker"}
                  key={"normal"}
                  onRenderItem={renderItemWithSecondaryText}
                  removeButtonAriaLabel={"Remove"}
                  inputProps={{
                    "aria-label": "People Picker",
                  }}
                  componentRef={picker}
                  onInputChange={onInputChange}
                  resolveDelay={300}
                  itemLimit={1}
                />
                <FormikErrorMessage name="pickerUser" />
              </Stack.Item>
              {canOnlyUpdateStatus && (
                <Stack.Item styles={fieldItemStyles}>
                  <FastField
                    component={FormikDropdown}
                    options={accessControlUserStatus}
                    onChange={(_, value): void => {
                      setFieldValue("status", value.key);
                    }}
                    label="Status"
                    name="status"
                    placeholder="Select Status"
                    styles={dropdownStyles}
                  />
                </Stack.Item>
              )}
            </Stack>
            <Stack tokens={verticalStackTokens} styles={formStackStyles}>
              {networkOperationError?.length > 0 && showError(networkOperationError)}
            </Stack>
            <SolidDivider />
            <Stack horizontal tokens={buttonStackTokens}>
              <Stack.Item>
                <PrimaryButton
                  text="Save"
                  styles={buttonStyles}
                  disabled={isSubmitting || isFormSubmitting || !isValid}
                  type="submit"
                />
              </Stack.Item>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

/**
 * Maps AccessControlUserForm to redux state
 * @param {AppState} state The App state.
 * @returns {Partial<IAccessControlUserFormProps>} AccessControlUserForm state mapped to props
 */
const mapStateToProps = (state: AppState): Partial<IAccessControlUserFormProps> => {
  return {
    networkOperationError: getNetworkOperationError(state.accessControlState),
  };
};

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch<AccessControlActions.AccessControlActionTypes>} dispatch map dispatch to props
 * @returns {Partial<IAccessControlUserFormProps>} map dispatch to props
 */
const mapDispatchToProps = (
  dispatch: Dispatch<AccessControlActions.AccessControlActionTypes>
): Partial<IAccessControlUserFormProps> => {
  return {
    actions: {
      createUser: bindActionCreators(AccessControlActions.createUser, dispatch),
      updateUserStatus: bindActionCreators(AccessControlActions.updateUserStatus, dispatch),
    },
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withRouter, withConnect)(AccessControlUserForm) as React.FC<any>;
